import {
  RCRM_FIELD_GROUP,
  type CustomFieldEntity,
  type LayoutEntity,
  type OverviewLayout,
} from '@crm/contracts';
import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { api, type FieldDefinitionView } from './client.ts';
import { useSession } from './session.tsx';

/**
 * Custom fields per record kind (RELAY-941; companies and projects since TS-1752). One cache
 * entry per kind, so the Fields page editing one kind refreshes every page that reads it.
 */
export const fieldsQuery = (entity: CustomFieldEntity) => ['field-definitions', entity] as const;
export const layoutQuery = (entity: LayoutEntity) => ['layout', entity] as const;

/** The kind's custom fields the viewer's role may see; none while `custom_fields.enabled` is off. */
export function useVisibleDefinitions(entity: CustomFieldEntity): FieldDefinitionView[] {
  const session = useSession();
  const on = session.isEnabled('custom_fields.enabled');
  const defs = useQuery({
    queryKey: fieldsQuery(entity),
    queryFn: () => api.fieldDefinitions.list(entity),
    enabled: on,
  });
  const role = session.me.organisation.role;
  return useMemo(
    () => (on ? (defs.data ?? []) : []).filter((d) => (d.visibility[role] ?? 'edit') !== 'hidden'),
    [on, defs.data, role],
  );
}

/** What a record shows for the groups the RecruitCRM import creates (TS-1753). */
export const ADDITIONAL_FIELDS = 'Additional Fields';

/**
 * A definition group as a record shows it; the stored name is unchanged (Settings → Fields and
 * the layout editor keep it). The import's groups — "RecruitCRM", and "RecruitCRM <entity>" for
 * fields moved to another record — read as "Additional Fields" on every entity.
 */
export function groupLabel(name: string): string {
  return name === RCRM_FIELD_GROUP || name.startsWith(`${RCRM_FIELD_GROUP} `)
    ? ADDITIONAL_FIELDS
    : name;
}

/**
 * Group the fields by the organisation's layout; whatever it omits goes last, never hidden:
 * custom fields under their definition's group (by position), then the kind's standard fields
 * under "Other". `standard` is what the kind's layout may place besides `custom.<key>`. Group
 * names are shown through `groupLabel`, groups that read the same are one block, and
 * "Additional Fields" is always the last block.
 */
export function arrangeFields(
  layout: OverviewLayout,
  definitions: readonly FieldDefinitionView[],
  standard: readonly string[],
): Array<{ name: string; fields: string[] }> {
  const placed = new Set(layout.groups.flatMap((g) => g.fields));
  const customKeys = definitions.map((d) => `custom.${d.key}`);
  const known = new Set([...standard, ...customKeys]);
  const groups: Array<{ name: string; fields: string[] }> = [];
  const add = (name: string, fields: readonly string[]) => {
    if (fields.length === 0) return;
    const label = groupLabel(name);
    const same = groups.find((g) => g.name === label);
    if (same) same.fields.push(...fields);
    else groups.push({ name: label, fields: [...fields] });
  };
  for (const g of layout.groups)
    add(
      g.name,
      g.fields.filter((f) => known.has(f)),
    );
  const leftoverCustom = definitions.filter((d) => !placed.has(`custom.${d.key}`));
  for (const group of [...new Set(leftoverCustom.map((d) => d.group ?? 'Custom fields'))]) {
    add(
      group,
      leftoverCustom
        .filter((d) => (d.group ?? 'Custom fields') === group)
        .sort((a, b) => a.position - b.position)
        .map((d) => `custom.${d.key}`),
    );
  }
  add(
    'Other',
    standard.filter((f) => !placed.has(f)),
  );
  const additional = groups.findIndex((g) => g.name === ADDITIONAL_FIELDS);
  if (additional !== -1) groups.push(...groups.splice(additional, 1));
  return groups;
}
